// Summary: Reusable numeric-entry modal used by POS quantity and diners workflows.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-pinpadmodal
import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDeleteLeft } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button } from '../Button';
import { NumericKeypad } from '../Keypad/NumericKeypad';
import classes from './num-pad.module.scss';

// Summary: Public modal contract for numeric entry with optional unlimited mode.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-pinpadmodal
export type NumPadModalProps = {
  isOpen: boolean;
  title: string;
  subjectName: string;
  initialValue?: number | null;
  allowUnlimited?: boolean;
  unlimitedLabel?: string;
  confirmLabel?: string;
  confirmDisabled?: boolean;
  // Disable confirm while the typed value is below this floor (e.g. diners require >= 1).
  minValue?: number;
  cancelLabel?: string;
  maxDigits?: number;
  onMaxDigitsExceeded?: () => void;
  // Opt-in transient inline notice shown at the display when maxDigits is exceeded.
  maxDigitsMessage?: string;
  isSubmitting?: boolean;
  error?: string | null;
  dataTestId?: string;
  onClose: () => void;
  onDismiss?: () => void;
  onConfirm: (value: number | null) => void | Promise<void>;
};

const INFINITY_SYMBOL = '∞';

// Reusable numeric-entry modal (twin of PinPadModal): null = unlimited, else a numeric buffer.
// Summary: Stateful numeric-entry surface used by diners/quantity editors.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-pinpadmodal
const NumPadModalComponent: React.FC<NumPadModalProps> = ({
  isOpen,
  title,
  subjectName,
  initialValue = null,
  allowUnlimited = true,
  unlimitedLabel = 'ללא הגבלה',
  confirmLabel = 'אישור',
  confirmDisabled = false,
  minValue,
  cancelLabel = 'ביטול',
  maxDigits = 6,
  onMaxDigitsExceeded,
  maxDigitsMessage,
  isSubmitting = false,
  error = null,
  dataTestId,
  onClose,
  onDismiss,
  onConfirm,
}) => {
  const unlimited = allowUnlimited;

  // null = unlimited; string = numeric buffer being edited.
  const [buffer, setBuffer] = useState<string | null>(() =>
    initialValue === null || initialValue === undefined ? (unlimited ? null : '') : String(initialValue),
  );
  // First keypress replaces the prefilled preview; refs avoid stale reads on rapid taps.
  const interactedRef = useRef(false);
  const bufferRef = useRef<string | null>(buffer);
  // Transient inline max-digits notice; clears on the next valid keypress.
  const [maxDigitsNotice, setMaxDigitsNotice] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const nextBuffer =
        initialValue === null || initialValue === undefined ? (unlimited ? null : '') : String(initialValue);
      bufferRef.current = nextBuffer;
      setBuffer(nextBuffer);
      interactedRef.current = false;
      setMaxDigitsNotice(false);
    }
  }, [isOpen, initialValue, unlimited]);

  useEffect(() => {
    bufferRef.current = buffer;
  }, [buffer]);

  if (!isOpen) {
    return null;
  }

  const pressDigit = (digit: string) => {
    if (isSubmitting) {
      return;
    }
    if (!interactedRef.current) {
      // First press: replace the preview with the chosen digit.
      interactedRef.current = true;
      bufferRef.current = digit;
      setBuffer(digit);
      setMaxDigitsNotice(false);
      return;
    }
    const base = bufferRef.current ?? '';
    const candidate = base + digit;
    if (candidate.replace('.', '').length > maxDigits) {
      onMaxDigitsExceeded?.();
      if (maxDigitsMessage) {
        setMaxDigitsNotice(true);
      }
      return;
    }
    // Strip a single leading zero so "0" + "5" becomes "5" rather than "05".
    const nextBuffer = candidate.replace(/^0(\d)/, '$1');
    bufferRef.current = nextBuffer;
    setBuffer(nextBuffer);
    setMaxDigitsNotice(false);
  };

  const pressBackspace = () => {
    if (isSubmitting) {
      return;
    }
    interactedRef.current = true;
    setMaxDigitsNotice(false);
    setBuffer((previous) => {
      if (previous === null || previous.length === 0) {
        return unlimited ? null : '';
      }
      const next = previous.slice(0, -1);
      return next.length === 0 ? (unlimited ? null : '') : next;
    });
  };

  const pressUnlimited = () => {
    if (isSubmitting || !unlimited) {
      return;
    }
    interactedRef.current = true;
    setBuffer(null);
  };

  const handleConfirm = () => {
    if (isSubmitting) {
      return;
    }
    const parsed = buffer === null || buffer.length === 0 ? null : Number(buffer);
    if (!unlimited && parsed === null) {
      return;
    }
    if (parsed !== null && !Number.isFinite(parsed)) {
      return;
    }
    if (minValue !== undefined && parsed !== null && parsed < minValue) {
      return;
    }
    void onConfirm(parsed);
  };

  const belowMinValue = minValue !== undefined && buffer !== null && buffer.length > 0 && Number(buffer) < minValue;

  return (
    <div
      className={classes.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid={dataTestId}
      onClick={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) {
          (onDismiss ?? onClose)();
        }
      }}
    >
      <div className={classes.modal}>
        <div className={classes.header}>
          <h2 className={classes.title}>{title}</h2>
        </div>

        <div className={classes.body}>
          {subjectName ? <p className={classes.name}>{subjectName}</p> : null}

          <div className={classes.display}>
            {buffer === null && unlimited ? (
              <span className={classes.displayInfinity}>{INFINITY_SYMBOL}</span>
            ) : (
              <span>{buffer && buffer.length > 0 ? buffer : '0'}</span>
            )}
          </div>

          {error && <p className={classes.error}>{error}</p>}
          {!error && maxDigitsNotice && maxDigitsMessage && (
            <p className={classes.error}>{maxDigitsMessage}</p>
          )}

          <NumericKeypad
            keypadClassName={classes.keypad}
            keyClassName={classes.key}
            disabled={isSubmitting}
            dataTestId={dataTestId ? `${dataTestId}-keypad` : undefined}
            onDigit={pressDigit}
            onBackspace={pressBackspace}
            backspaceDisabled={buffer === null || buffer.length === 0}
            backspaceAriaLabel="מחק ספרה"
            backspaceIcon={<FontAwesomeIcon icon={faDeleteLeft as IconProp} />}
            leftKey={{
              label: unlimited ? INFINITY_SYMBOL : '',
              className: unlimited ? `${classes.key} ${classes.keyAccent}` : classes.key,
              onClick: unlimited ? pressUnlimited : undefined,
              disabled: !unlimited,
              ariaLabel: unlimited ? unlimitedLabel : undefined,
              ariaHidden: !unlimited,
              tabIndex: unlimited ? undefined : -1,
              title: unlimited ? unlimitedLabel : undefined,
            }}
          />

          <div className={classes.actions}>
            <Button
              variant="cancel"
              fullWidth
              onClick={onClose}
              disabled={isSubmitting}
              dataTestId={dataTestId ? `${dataTestId}-cancel` : undefined}
            >
              {cancelLabel}
            </Button>
            <Button
              variant="confirm"
              fullWidth
              onClick={handleConfirm}
              disabled={isSubmitting || confirmDisabled || belowMinValue || (!allowUnlimited && (buffer === null || buffer.length === 0))}
              dataTestId={dataTestId ? `${dataTestId}-confirm` : undefined}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const NumPadModal = React.memo(NumPadModalComponent);
